import * as React from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {Provider as ThemeProvider} from '@sail/theme-classic';
import {css, view} from '@sail/ui/deprecated';
import type {PressableCoreWidth} from '@stripe-internal/sail/Pressable/types';
import {
  SearchableSelect,
  SearchableSelectEmpty,
  SearchableSelectResultProps,
  useSearch,
} from '@stripe-internal/sail/Search';
import {getTopicOptions} from './getTopicOptions';
import type {TopicOption} from './getTopicOptions';
import {searchableTextFromOption, createTopicQuery} from './search';
import {selectorMessages} from './selectorMessages';
import {Topic} from './TopicSelector';
import {useClientAnalytics} from '../../Providers/ClientProvider/useClientAnalytics';

const buttonLabelForOption = (option: TopicOption): string => option.label;
const formValueForOption = (option: TopicOption): string => option.value;

// eslint-disable-next-line @stripe-internal/stripe/no-anonymous-jsx-functions
const renderOption = (
  props: SearchableSelectResultProps<TopicOption>,
): JSX.Element => (
  <ThemeProvider>
    <view.span>{props.entry.label}</view.span>
  </ThemeProvider>
);

// eslint-disable-next-line @stripe-internal/stripe/no-anonymous-jsx-functions
const renderEmpty = (): JSX.Element => (
  <SearchableSelectEmpty>
    <FormattedMessage {...selectorMessages.noResults} />
  </SearchableSelectEmpty>
);

// eslint-disable-next-line @stripe-internal/stripe/no-anonymous-jsx-functions
const renderError = (): JSX.Element => (
  <ThemeProvider>
    <view.div css={{padding: css.value('8px')}}>
      <view.span css={{color: 'critical'}}>
        <FormattedMessage {...selectorMessages.searchError} />
      </view.span>
    </view.div>
  </ThemeProvider>
);

type Props = {
  onSelectTopic: (topic?: Topic) => void;
  selectedTopic: Topic | null | undefined;
  topics: ReadonlyArray<Topic>;
  width?: PressableCoreWidth;
  onBlur?: () => void;
  invalid?: boolean;
};

export const TopicSelect = ({
  onSelectTopic,
  selectedTopic,
  topics,
  width,
  onBlur,
  invalid,
}: Props): JSX.Element => {
  const intl = useIntl();
  const analytics = useClientAnalytics();

  const options: Array<TopicOption> = React.useMemo(
    () => getTopicOptions(intl, topics),
    [intl, topics],
  );

  const selectedOption = React.useMemo(
    () => options.find((o) => o.value === selectedTopic),
    [options, selectedTopic],
  );

  const handleSelect = React.useCallback(
    (option?: TopicOption | null): void => {
      analytics.action('topic_selected', {topic: option?.value});
      onSelectTopic(option?.value);
    },
    [onSelectTopic, analytics],
  );

  const query = React.useMemo(() => createTopicQuery(options), [options]);
  const searchProps = useSearch(query);

  return (
    <SearchableSelect
      {...searchProps}
      name="topic"
      chooseLabel={intl.formatMessage(selectorMessages.chooseLabel)}
      inputPlaceholder={intl.formatMessage(selectorMessages.inputPlaceholder)}
      getSearchableText={searchableTextFromOption}
      onChange={handleSelect}
      value={selectedOption}
      width={width}
      renderItem={renderOption}
      renderEmpty={renderEmpty}
      renderError={renderError}
      getButtonLabel={buttonLabelForOption}
      getFormValue={formValueForOption}
      onBlur={onBlur}
      invalid={invalid}
    />
  );
};
